// Lib
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash/fp';

// Utils
import { retrieveDeviceId } from '../../../device/deviceService';
import { authenticateWithApple, authenticateWithGoogle } from '../../authUtils';
import { isPlatformIframe } from '../../../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';
import { isGuestSelector } from '../../../user/currentUserSelector';

// Actions
import { login } from '../../authActions';
import { redirectLoggedInUserToWorkspace } from '../../../reducers/navigationActions';
import { getCurrentUserToken, getHasRememberedBetterAuthSessionUser } from '../../authService';

// Roma Components
import { Button, SocialButton, ContentDivider } from '@milanote/roma';

// Components
import RomaAuthModal from '../RomaAuthModal';
import LoginForm from './LoginForm';
import AuthFormDisclaimerSection from '../../AuthFormDisclaimerSection';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { RomaAuthContainer, showOverlay } from '../RomaAuthContainer';
import useAuthLocaleManager from '../../localisation/useAuthLocaleManager';
import AuthLocaleSelect from '../../localisation/AuthLocaleSelect';

// Constants
import { AUTH_FLOWS } from '../../../../common/utils/authConstants';

// Styles

const getRegisterPath = (route) => get(['registerPath'], route) || 'register';

// Redux connect methods
const Login = ({ location, close, route }) => {
    const isGuest = useSelector(isGuestSelector);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const translate = useTranslator('auth', 'login');
    const { localeOptions, selectedLocale, onSelectLocale, hasResolvedLocale } = useAuthLocaleManager({ location });

    const deviceId = useMemo(retrieveDeviceId, []);
    const hasRenderedAsGuestLogin = useRef(false);
    const hasRememberedBetterAuthSessionUser = getHasRememberedBetterAuthSessionUser();
    const shouldRedirectLoggedInUser = !isGuest && (!!getCurrentUserToken() || hasRememberedBetterAuthSessionUser);

    const dispatch = useDispatch();
    const dispatchLogin = ({ email, password, ...rest }) =>
        dispatch(
            login(email, password, {
                ...rest,
                appLocale: selectedLocale || rest.appLocale,
            }),
        );
    const navigateToWorkspace = () => dispatch(redirectLoggedInUserToWorkspace());

    // When an already signed-in user opens /login directly, restore them to the workspace.
    // Once this page has rendered as a real guest login, leave successful-login navigation to the login flow.
    useEffect(() => {
        if (isGuest) {
            hasRenderedAsGuestLogin.current = true;
            return;
        }

        if (hasRenderedAsGuestLogin.current) return;
        if (!shouldRedirectLoggedInUser) return;

        navigateToWorkspace();
    }, [isGuest, shouldRedirectLoggedInUser]);

    if (isPlatformIframe(platformDetails)) return null;
    if (!hasResolvedLocale) return null;

    return (
        <RomaAuthContainer overlay={showOverlay(route)} close={close} styled={false}>
            <div className="flex flex-col gap-3xl">
                {/* Header */}
                <RomaAuthModal.Header title={translate('heading')} />

                {/* Social auth */}
                <div className="flex flex-col gap-lg">
                    <SocialButton
                        social="google"
                        size="md"
                        theme="color"
                        onClick={() =>
                            authenticateWithGoogle({
                                flow: AUTH_FLOWS.SIGN_IN,
                                deviceId,
                                appLocale: selectedLocale || undefined,
                            })
                        }
                    >
                        {translate('social.googleButton')}
                    </SocialButton>

                    <SocialButton
                        social="apple"
                        size="md"
                        theme="color"
                        onClick={() =>
                            authenticateWithApple({
                                flow: AUTH_FLOWS.SIGN_IN,
                                deviceId,
                                appLocale: selectedLocale || undefined,
                            })
                        }
                    >
                        {translate('social.appleButton', 'Log in with Apple')}
                    </SocialButton>
                </div>

                <ContentDivider type="single-line" className="-mb-md uppercase">
                    {translate('dividerLabel')}
                </ContentDivider>

                <LoginForm deviceId={deviceId} location={location} dispatchLogin={dispatchLogin} />

                <div className="text-center text-md text-fg-secondary">
                    {translate('register.text')}{' '}
                    <Button
                        href={`/${getRegisterPath(route)}`}
                        routerOptions={{ keepParams: true }}
                        color="link-color"
                        size="md"
                    >
                        {translate('register.link')}
                    </Button>
                </div>

                <AuthFormDisclaimerSection />
                <AuthLocaleSelect
                    selectedLocale={selectedLocale}
                    localeOptions={localeOptions}
                    onSelectLocale={onSelectLocale}
                />
            </div>
        </RomaAuthContainer>
    );
};

Login.propTypes = {
    route: PropTypes.object,
    location: PropTypes.object,
    close: PropTypes.func,
};

export default Login;
